import PropTypes from 'prop-types';
import React from 'react';
import Griddle, { plugins, RowDefinition, ColumnDefinition } from 'griddle-react';
import { ButtonGroup, Button } from 'react-bootstrap';

import { displayBuyerBidState, displayBidderBidState } from '../../../core/api';
import { findById } from '../../../core/utils';
import { fullDate } from '../../../core/date';
import BidDetailModal from './BidDetailModal';
import BidUpdateModal from './BidUpdateModal';
import HideButton from './HideButton';

const styleConfig = {
  icons: {
    TableHeadingCell: {
      sortDescendingIcon: '▼',
      sortAscendingIcon: '▲',
    },
  },
  classNames: {
    Row: 'row-class',
    Table: 'table-striped table',
    Filter: 'bid-filter',
  },
  styles: {
    Filter: { fontSize: 18 },
  },
};
const pageProperties = {
  currentPage: 1,
  pageSize: 200,
};
const sortProperties = [
  { id: 'start', sortAscending: true },
  { id: 'group', sortAscending: true },
  { id: 'name', sortAscending: true },
];

const dateDisplay = ({ value }) => {
  return (
    <span> {fullDate(value)} </span>
  )
}
// DR: 'Draft',
// UK: 'Unsent',
// SG: 'Sending',
// ST: 'Sent',
// UR: 'Unread',
// VW: 'Viewed',
// RE: 'Replied',
// PD: 'Pending',
// NB: 'Nobid',
// IN: 'Interested',
// DE: 'Decline',
// DD: 'Declined',
// EX: 'Expired',
// AC: 'Accept',
// AD: 'Accepted',
// CA: 'Cancel',
// CD: 'Cancelled',
// CP: 'Contract Pending',
// CO: 'Contracted',
function displayBidState(buyerState, bidderState) {
  if (['ST'].includes(buyerState)) {
    return `${displayBuyerBidState(buyerState, bidderState)} (${displayBidderBidState(
      buyerState,
      bidderState
    )})`;
  }
  return displayBuyerBidState(buyerState, bidderState);
}

class BidsetsTable extends React.Component {
  constructor(props) {
    super(props);
    if (props.detail) {
      this.state = {
        showBid: true,
        bid: props.detail,
      };
    } else {
      this.state = {
        showBid: false,
      };
    }
    this.showBid = this.showBid.bind(this);
    this.closeBid = this.closeBid.bind(this);
  }
  closeBid() {
    this.setState({
      showBid: false,
    });
  }
  showBid(e) {
    e.preventDefault();
    const id = e.currentTarget.getAttribute('data-item');
    if (id) {
      this.setState({
        bid: id,
        showBid: true,
      });
    }
  }
  findBid(id) {
    const idx = this.props.bids.findIndex(findById, id);
    if (idx === -1) {
      console.log('unable to find bid...', id);
      return {};
    }
    return this.props.bids[idx];
  }

  render() {
    const ButtonColumn = ({ value }) => (
      <ButtonGroup className="manage-bids-btnGroup">
        <Button bsStyle="default" data-item={value} onClick={this.showBid}>
          <i className="fa fa-pencil fa-lg" /> Manage Bid
        </Button>
        {!this.props.isTourManager && (
          <HideButton bid={this.findBid(value)} editBid={this.props.editBid} />
        )}
      </ButtonGroup>
    );
    const bids = [];

    this.props.bids.forEach(bid => {
      // Filter bids with filter or hardcoded filter for tour manager
      if (this.props.filter[bid.buyer_state]) {
        return;
      }
      if (this.props.filter[bid.bidset.event.group_name]) {
        return;
      }

      // Don't show hidden bids to Tour Manager
      if (this.props.isTourManager && !bid.share) {
        return;
      }

      const displayState = displayBidState(bid.buyer_state, bid.bidder_state);
      const newbid = {
        id: bid.id,
        name: bid.hotel.name,
        group: bid.bidset.event.group_name,
        location: `${bid.hotel.locality}, ${bid.hotel.region}`,
        distance: parseFloat(bid.distance_to_venue * 0.621371).toFixed(1),
        start: bid.bidset.event.start,
        end: bid.bidset.event.end,
        state: displayState,
      };
      if (bid.suite_rate) {
        newbid.suite = bid.suite_rate;
      }
      if (bid.king_rate) {
        newbid.king = bid.king_rate;
      }
      if (bid.queen_rate) {
        newbid.queen = bid.queen_rate;
      }
      bids.push(newbid);
    });
    return (
      <div>
        <Griddle
          data={bids}
          plugins={[plugins.LocalPlugin]}
          styleConfig={styleConfig}
          pageProperties={pageProperties}
          sortProperties={sortProperties}
          components={{
            SettingsToggle: () => <span />,
          }}
        >
          <RowDefinition>
            <ColumnDefinition id="name" title="Hotel" order={1} />
            <ColumnDefinition id="group" title="Group" order={2} />
            <ColumnDefinition id="location" title="Location" order={3} />
            <ColumnDefinition id="distance" title="Distance" order={3} />
            <ColumnDefinition id="start" title="Check In" order={4} customComponent={dateDisplay} />
            <ColumnDefinition id="end" title="Check Out" order={5} customComponent={dateDisplay} />
            <ColumnDefinition id="state" title="Status" order={6} />
            <ColumnDefinition id="suite" title="Suite" order={7} />
            <ColumnDefinition id="king" title="King" order={8} />
            <ColumnDefinition id="queen" title="Doubles" order={9} />
            <ColumnDefinition id="id" title=" " order={10} customComponent={ButtonColumn} />
          </RowDefinition>
        </Griddle>
        {this.state.showBid && (
          <BidDetailModal
            bid={this.findBid(this.state.bid)}
            show={this.state.showBid}
            onClose={this.closeBid}
            addBidNote={this.props.addBidNote}
            editBid={this.props.editBid}
            sendBid={this.props.sendBid}
            removeBid={this.props.removeBid}
            isTourManager={this.props.isTourManager}
            readOnly={this.props.readOnly}
            user={this.props.user}
          />
        )}
        {this.props.showUpdate && (
          <BidUpdateModal
            bids={bids}
            show={this.props.showUpdate}
            onClose={this.props.closeUpdate}
            sendBidUpdate={this.props.sendBidUpdate}
            user={this.props.user}
          />
        )}
      </div>
    );
  }
}

BidsetsTable.propTypes = {
  bids: PropTypes.arrayOf(PropTypes.object).isRequired,
  isTourManager: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  detail: PropTypes.string,
  editBid: PropTypes.func.isRequired,
  addBidNote: PropTypes.func.isRequired,
  sendBid: PropTypes.func.isRequired,
  removeBid: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  showUpdate: PropTypes.bool.isRequired,
  closeUpdate: PropTypes.func.isRequired,
  sendBidUpdate: PropTypes.func.isRequired,
};

BidsetsTable.defaultProps = {
  bids: [],
  detail: null,
};
export default BidsetsTable;



// WEBPACK FOOTER //
// src/routes/tours/components/BidsetsTable.js